import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { useDataChainId } from '@ui/hooks/datachain';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { useMemo } from 'react';
import { Address } from 'viem';

import { pairInfoControllerApi } from '../clients/clients';
import { GetVolumeRangeEnum, GetVolumeRequest } from '../generated/apis';
import * as runtime from '../generated/runtime';
import { toServerBlockChain } from '../utils';

export const useBaseTradePairsVolume = ({
  pairBaseAddresses,
  range,
}: {
  pairBaseAddresses: Address[];
  range: GetVolumeRequest['range'];
}) => {
  const chainId = useDataChainId();
  const tradePairsVolumeQueries = useMemo(() => {
    return pairBaseAddresses.map((pairBaseAddress) => {
      return {
        queryKey: getKey({
          pairBaseAddress,
          range,
          blockChain: toServerBlockChain(chainId),
        }),
        queryFn: getFn({
          pairBaseAddress,
          range,
          blockChain: toServerBlockChain(chainId),
        }),
      };
    });
  }, [chainId, pairBaseAddresses, range]);

  return useQueries({
    queries: tradePairsVolumeQueries,
    combine(result) {
      const isPending = result.some(
        (query) => query.isLoading || query.isFetching,
      );

      const data = result.reduce(
        (acc, query) => {
          if (query.data) {
            acc.volumeSum += query.data.volume;
            acc.volumeUSDSum += query.data.volumeUSD;
          }

          return acc;
        },
        { volumeSum: 0, volumeUSDSum: 0 },
      );

      return { data, isPending };
    },
  });
};

export const useTradePairsVolume = ({
  range = GetVolumeRangeEnum.OneDay,
}: {
  range?: GetVolumeRequest['range'];
} = {}) => {
  const chainId = useDataChainId();

  const pairs = useTradePairInfos();

  const tradePairAddresses = pairs.map((v) => v.address);

  const tradePairsVolumeQueries = useMemo(() => {
    return tradePairAddresses.map((tradePairAddress) => {
      return {
        queryKey: getKey({
          pairBaseAddress: tradePairAddress,
          range,
          blockChain: toServerBlockChain(chainId),
        }),
        queryFn: getFn({
          pairBaseAddress: tradePairAddress,
          range,
          blockChain: toServerBlockChain(chainId),
        }),
      };
    });
  }, [tradePairAddresses, range, chainId]);

  return useQueries({
    queries: tradePairsVolumeQueries,
    combine(results) {
      const isPending = results.some(
        (query) => query.isLoading || query.isFetching,
      );

      // Group results by baseSymbol
      const groupedData = results.reduce((acc, result, index) => {
        const currentPair = pairs[index];
        const baseSymbol = currentPair.baseSymbol;

        if (!acc[baseSymbol]) {
          acc[baseSymbol] = {
            volume: 0,
            volumeUSD: 0,
            pairBase: currentPair.isDegenPair ? '' : currentPair.address,
            symbol: baseSymbol,
          };
        }

        // Only update pairBase if current pair is not a degen pair
        if (!currentPair.isDegenPair) {
          acc[baseSymbol].pairBase = currentPair.address;
        }

        // Sum up volumes
        if (result.data) {
          acc[baseSymbol].volume += result.data.volume ?? 0;
          acc[baseSymbol].volumeUSD += result.data.volumeUSD ?? 0;
        }

        return acc;
      }, {} as Record<string, { volume: number; volumeUSD: number; pairBase: string; symbol: string }>);

      // Convert back to array
      const data = Object.values(groupedData);

      return { data, isPending };
    },
  });
};

export const useTradePairsVolumeByAddresses = ({
  pairBaseAddresses,
  range = GetVolumeRangeEnum.OneDay,
}: {
  pairBaseAddresses: Address[];
  range?: GetVolumeRequest['range'];
}) => {
  const chainId = useDataChainId();
  const pairs = useTradePairInfos();

  // Filter relevant pairs based on input addresses' symbols
  const targetPairs = useMemo(() => {
    if (!pairs || pairBaseAddresses.length === 0) return [];

    const targetSymbols = new Set<string>();

    // 1. Find symbols for input addresses
    pairBaseAddresses.forEach((addr) => {
      const found = pairs.find(
        (p) => p.address.toLowerCase() === addr.toLowerCase(),
      );
      if (found) targetSymbols.add(found.baseSymbol);
    });

    // 2. Return all pairs that match these symbols (including Degen pairs)
    return pairs.filter((p) => targetSymbols.has(p.baseSymbol));
  }, [pairs, pairBaseAddresses]);

  const queries = useMemo(() => {
    return targetPairs.map((pair) => ({
      queryKey: getKey({
        pairBaseAddress: pair.address,
        range,
        blockChain: toServerBlockChain(chainId),
      }),
      queryFn: getFn({
        pairBaseAddress: pair.address,
        range,
        blockChain: toServerBlockChain(chainId),
      }),
    }));
  }, [targetPairs, range, chainId]);

  return useQueries({
    queries,
    combine(results) {
      const isPending = results.some(
        (query) => query.isLoading || query.isFetching,
      );

      const groupedData = results.reduce((acc, result, index) => {
        const currentPair = targetPairs[index];
        const baseSymbol = currentPair.baseSymbol;

        if (!acc[baseSymbol]) {
          acc[baseSymbol] = {
            volume: 0,
            volumeUSD: 0,
            pairBase: currentPair.isDegenPair ? '' : currentPair.address,
            symbol: baseSymbol,
          };
        }

        if (!currentPair.isDegenPair) {
          acc[baseSymbol].pairBase = currentPair.address;
        }

        if (result.data) {
          acc[baseSymbol].volume += result.data.volume ?? 0;
          acc[baseSymbol].volumeUSD += result.data.volumeUSD ?? 0;
        }

        return acc;
      }, {} as Record<string, { volume: number; volumeUSD: number; pairBase: string; symbol: string }>);

      const data = Object.values(groupedData);

      return { data, isPending };
    },
  });
};

export const useTradePairVolume = (requestParameters: GetVolumeRequest) => {
  const reactQueryOptions = useMemo(() => {
    return queryOptions({
      queryKey: getKey(requestParameters),
      queryFn: getFn(requestParameters),
    });
  }, [requestParameters]);

  return useQuery(reactQueryOptions);
};

const getKey = (requestParameters: GetVolumeRequest) => {
  return ['be-api/trade-pair/volume', requestParameters];
};
const getFn =
  (
    requestParameters: GetVolumeRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ) =>
  () =>
    pairInfoControllerApi.getVolume(requestParameters, initOverrides);
useTradePairVolume.getQueryKey = getKey;
useTradePairVolume.getQueryFn = getFn;
